import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CommentIcon from "@mui/icons-material/Comment";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BarChartIcon from "@mui/icons-material/BarChart";
import { ISidekickSession, ISidekickTurn } from "../../types/api/sidekick-usage.type";
import {
  CLIENT_LABELS,
  USER_KIND_LABELS,
  formatCount,
  formatDateTime,
  formatLatency,
} from "./labels";

const ANSWER_PREVIEW_CHARS = 400;

const preStyle = {
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
  fontFamily: "inherit",
  margin: 0,
};

const TurnChips = ({ turn }: { turn: ISidekickTurn }) => (
  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ rowGap: 1 }}>
    {turn.rating === "up" && (
      <Chip size="small" color="success" icon={<ThumbUpIcon />} label="Thumbs up" />
    )}
    {turn.rating === "down" && (
      <Chip size="small" color="error" icon={<ThumbDownIcon />} label="Thumbs down" />
    )}
    {!turn.rating && <Chip size="small" variant="outlined" label="Not rated" />}
    {turn.flags?.budget_exceeded && (
      <Chip size="small" color="warning" icon={<WarningAmberIcon />} label="Ran out of tool budget" />
    )}
    {(turn.flags?.answer_guards ?? []).map((code) => (
      <Chip
        key={code}
        size="small"
        color="warning"
        icon={<WarningAmberIcon />}
        label={`Answer guard warning: ${code}`}
      />
    ))}
    {turn.flags?.charts > 0 && (
      <Chip
        size="small"
        variant="outlined"
        icon={<BarChartIcon />}
        label={turn.flags.charts === 1 ? "1 chart" : `${turn.flags.charts} charts`}
      />
    )}
    {turn.client && (
      <Chip size="small" variant="outlined" label={CLIENT_LABELS[turn.client] ?? turn.client} />
    )}
    <Chip size="small" variant="outlined" label={`Answered in ${formatLatency(turn.latency_ms)}`} />
  </Stack>
);

const Turn = ({ turn }: { turn: ISidekickTurn }) => {
  const [showFullAnswer, setShowFullAnswer] = useState(false);
  const [showTrace, setShowTrace] = useState(false);
  const answer = turn.answer ?? "";
  const isLong = answer.length > ANSWER_PREVIEW_CHARS;
  const shownAnswer =
    isLong && !showFullAnswer ? `${answer.slice(0, ANSWER_PREVIEW_CHARS)}…` : answer;

  return (
    <Box sx={{ py: 2 }} data-testid="turn">
      <Typography variant="caption" color="#999">
        {formatDateTime(turn.created_at)}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Question
      </Typography>
      <Typography component="pre" sx={preStyle}>
        {turn.question}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
        Answer
      </Typography>
      <Typography component="pre" sx={{ ...preStyle, color: "#ccc" }}>
        {shownAnswer || "(no answer)"}
      </Typography>
      {isLong && (
        <Button size="small" onClick={() => setShowFullAnswer((v) => !v)}>
          {showFullAnswer ? "Show less" : "Show full answer"}
        </Button>
      )}
      {turn.comment && (
        <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "flex-start" }}>
          <CommentIcon fontSize="small" sx={{ mt: "2px" }} />
          <Typography component="pre" sx={{ ...preStyle, fontStyle: "italic" }}>
            {turn.comment}
          </Typography>
        </Box>
      )}
      <Box sx={{ mt: 1 }}>
        <TurnChips turn={turn} />
      </Box>
      <Button size="small" sx={{ mt: 1 }} onClick={() => setShowTrace((v) => !v)}>
        {showTrace ? "Hide technical details" : "Show technical details"}
      </Button>
      {showTrace && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="#999">
            Model: {turn.model ?? "—"} · Tokens in/out: {formatCount(turn.input_tokens)} /{" "}
            {formatCount(turn.output_tokens)} · Tools used:{" "}
            {turn.tools?.length ? turn.tools.join(", ") : "none"} · Message ID: {turn.message_id}
          </Typography>
          <Box
            component="pre"
            data-testid="trace-json"
            sx={{
              backgroundColor: "#18191D",
              p: 1.5,
              borderRadius: 1,
              overflow: "auto",
              maxHeight: 400,
              fontSize: 12,
            }}
          >
            {JSON.stringify(turn.trace ?? null, null, 2)}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const SessionList = ({ sessions }: { sessions: ISidekickSession[] }) => {
  if (!sessions.length) {
    return <Typography color="#999">No conversations match these filters.</Typography>;
  }
  return (
    <>
      {sessions.map((session) => (
        <Accordion
          key={`${session.env_id}-${session.session_id}`}
          disableGutters
          TransitionProps={{ unmountOnExit: true }}
          sx={{ backgroundColor: "#2A2B31", backgroundImage: "none", mb: 1 }}
          data-testid="session"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", width: "100%" }}>
              <Typography sx={{ minWidth: 150 }}>{formatDateTime(session.started_at)}</Typography>
              <Typography sx={{ fontWeight: 600 }}>{session.plant}</Typography>
              <Typography color="#ccc">{session.user_email}</Typography>
              <Chip
                size="small"
                variant="outlined"
                label={USER_KIND_LABELS[session.user_kind] ?? session.user_kind}
              />
              <Typography color="#999" sx={{ ml: "auto", pr: 1 }}>
                {session.turns.length === 1 ? "1 question" : `${session.turns.length} questions`}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {session.turns.map((turn, i) => (
              <Box key={turn.message_id || i}>
                {i > 0 && <Divider />}
                <Turn turn={turn} />
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
};

export default SessionList;
